import * as _ from 'underscore';

import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import { IBulkImportUsersFromCsvResult, IImportUserFromCSVError, IImportUserFromCSVResult } from '../ServerApi/types/BulkImportUsersFromCsvResponse';
import { UserActionResultCode } from '../../userManagement/services/UserActionResultCode';
import { ServiceLocator } from '../../app/ServiceLocator';

export interface IBulkImportResultsSummaryScope extends ng.IScope {
  results: IBulkImportUsersFromCsvResult;

  usersSkippedMessage: string;
  usersProcessedMessage: string;

  successResults: CodeUsernamesCountMessage[];
  errorResults: CodeUsernamesCountMessage[];
}

export interface CodeUsernamesCount {code: number; usernames: string; count: number; message?: string;}
export interface CodeUsernamesCountMessage extends CodeUsernamesCount { message: string; }
export interface ErrorWithLocalizedMessage extends IImportUserFromCSVError { message: string; }

Module.instance().directive('tbBulkImportResultsSummary', [
  '$translate',
  ($translate: ng.translate.ITranslateService) => {
    return {
      restrict: 'E',
      replace: true,
      scope: {
        results: '='
      },
      templateUrl: 'bulkImportResultsSummary.html',
      link: (scope: IBulkImportResultsSummaryScope) => {
        scope.$watch('results', () => {
          parseResults();
        });

        function parseResults() {
          if (!scope.results) return;

          scope.usersSkippedMessage = $translate.instant(
                'userActionBulkOperationNumberSkipped', { COUNT: scope.results.skippedLineCount }, L10n.Interpolations.Count);
          scope.usersProcessedMessage = $translate.instant(
                'userActionBulkOperationNumberProcessed', { COUNT: scope.results.processedLineCount }, L10n.Interpolations.Count);

          const successResultsByCode = aggregateByResultCode(scope.results.results);
          scope.successResults = addLocalizedResultMessage(successResultsByCode);

          const errorResultsByCode = aggregateByErrorCode(scope.results.errors);
          scope.errorResults = addLocalizedErrorMessage(errorResultsByCode);
        }

        function aggregateByResultCode(results: IImportUserFromCSVResult[]): _.Dictionary<CodeUsernamesCount> {
          const successResultsByCode: _.Dictionary<CodeUsernamesCount> = {};
          // aggregate success results by result code
          _.each(results, (user: IImportUserFromCSVResult) => {
            _.each(user.codes, (code: number) => {
              if (!successResultsByCode[code]) {
                successResultsByCode[code] = {
                  code: code,
                  usernames: user.username,
                  count: 1
                };
              } else {
                successResultsByCode[code].usernames += ', ' + user.username;
                ++successResultsByCode[code].count;
              }
            });
          });
          return successResultsByCode;
        }

        function addLocalizedResultMessage(aggregatedResults: _.Dictionary<CodeUsernamesCount>): CodeUsernamesCountMessage[] {
          return _.map(aggregatedResults, (value: CodeUsernamesCount) =>
            _.extend(value, {
              message: UserActionResultCode.instance.toLocalizedResultMessage(value.code, value.count)
            })
          );
        }

        function addLocalizedErrorMessage(aggregatedResults: _.Dictionary<CodeUsernamesCount>): CodeUsernamesCountMessage[] {
          return _.map(aggregatedResults, (value: CodeUsernamesCount) =>
            _.extend(value, {
              message: UserActionResultCode.instance.toLocalizedErrorMessage(value.code, value.count, getSiteRole(value.message))
            })
          );
        }

        function getSiteRole(message: string) {
          if(message && message.match('role')){
            return message.split(':')[1].split('(')[0];
          }
        }

        function aggregateByErrorCode(errors: IImportUserFromCSVError[]): _.Dictionary<CodeUsernamesCount> {
          const errorResultsByCode: _.Dictionary<CodeUsernamesCount> = {};
          _.each(errors, (error: IImportUserFromCSVError) => {
            if (!error.username) {
              // for some errors (like UsernameMissing) there is no username.
              // So, show the line number were we would usually show the username.
              error.username = $translate.instant(
                'userActionBulkOperationErrorLineNumber', { LINE_NUMBER: error.line }, L10n.Interpolations.Count);
            }
            const code = error.errorCode;
            if (!errorResultsByCode[code]) {
              errorResultsByCode[code] = {
                code: code,
                usernames: error.username,
                count: 1,
                message: error.message,
              };
            } else {
              errorResultsByCode[code].usernames += ', ' + error.username;
              ++errorResultsByCode[code].count;
            }
          });
          return errorResultsByCode;
        }

      }
    };
  }
]);
